import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';

import {
  createScheduledEmail,
  deleteScheduledEmail,
  listScheduledEmails,
  transitionStatus,
  updateScheduledEmail,
  type ScheduledEmail,
  type ScheduledEmailInput,
  type ScheduledEmailPlatform,
  type ScheduledEmailStatus,
} from '../../services/scheduledEmailService';
import clientService from '../../services/clientService';
import type { Client } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants / helpers
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_ORDER: ScheduledEmailStatus[] = [
  'planned',
  'created',
  'verified',
  'scheduled',
  'sent',
  'canceled',
];

const STATUS_COLOR: Record<ScheduledEmailStatus, { bg: string; fg: string; border: string }> = {
  planned:   { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' },
  created:   { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  verified:  { bg: '#ccfbf1', fg: '#0f766e', border: '#5eead4' },
  scheduled: { bg: '#fed7aa', fg: '#9a3412', border: '#fdba74' },
  sent:      { bg: '#bbf7d0', fg: '#166534', border: '#86efac' },
  canceled:  { bg: '#fecaca', fg: '#991b1b', border: '#fca5a5' },
};

const STATUS_LABEL: Record<ScheduledEmailStatus, string> = {
  planned: 'Planned',
  created: 'Created',
  verified: 'Verified',
  scheduled: 'Scheduled',
  sent: 'Sent',
  canceled: 'Canceled',
};

const PLATFORMS: { value: ScheduledEmailPlatform; label: string }[] = [
  { value: 'mautic', label: 'Mautic' },
  { value: 'brevo', label: 'Brevo' },
  { value: 'other', label: 'Other' },
];

function fmtIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function fmtIsoLocalDateTime(d: Date): string {
  // for <input type="datetime-local">; trim seconds
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

function parseIsoSafe(s: string | null | undefined): Date | null {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Recurrence (UI-only; backend stores each occurrence as an independent row)
// ─────────────────────────────────────────────────────────────────────────────

type RecurrenceFreq = 'daily' | 'weekly' | 'monthly';

interface RecurrenceState {
  enabled: boolean;
  freq: RecurrenceFreq;
  weekday: number;     // 0=Sun..6=Sat (weekly only)
  dayOfMonth: number;  // 1-31 (monthly only)
  until: string;       // yyyy-MM-dd, inclusive
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MAX_OCCURRENCES = 366; // safety cap

function defaultRecurrenceFor(start: Date): RecurrenceState {
  return {
    enabled: false,
    freq: 'weekly',
    weekday: start.getDay(),
    dayOfMonth: start.getDate(),
    until: '',
  };
}

/** Expand a recurrence rule to the list of send-date Date objects. Time-of-day is taken from `start`. */
function expandRecurrence(start: Date, rec: RecurrenceState): Date[] {
  if (!rec.enabled) return [start];
  if (!rec.until) return [];
  const endDate = parseISO(rec.until);
  if (isNaN(endDate.getTime())) return [];
  const limit = endOfDay(endDate);

  const hh = start.getHours();
  const mm = start.getMinutes();
  const out: Date[] = [];

  const push = (d: Date) => {
    const occ = new Date(d);
    occ.setHours(hh, mm, 0, 0);
    if (!isAfter(occ, limit) && out.length < MAX_OCCURRENCES) out.push(occ);
  };

  if (rec.freq === 'daily') {
    let d = startOfDay(start);
    while (!isAfter(d, limit) && out.length < MAX_OCCURRENCES) {
      push(d);
      d = addDays(d, 1);
    }
  } else if (rec.freq === 'weekly') {
    let d = startOfDay(start);
    // Advance to the first occurrence of the chosen weekday at or after start.
    while (d.getDay() !== rec.weekday) d = addDays(d, 1);
    while (!isAfter(d, limit) && out.length < MAX_OCCURRENCES) {
      push(d);
      d = addDays(d, 7);
    }
  } else if (rec.freq === 'monthly') {
    let yr = start.getFullYear();
    let mo = start.getMonth();
    // If this month's chosen day is already past, jump to next month.
    if (start.getDate() > rec.dayOfMonth) {
      mo += 1;
      if (mo > 11) { mo = 0; yr += 1; }
    }
    while (out.length < MAX_OCCURRENCES) {
      const lastDay = new Date(yr, mo + 1, 0).getDate();
      const day = Math.min(rec.dayOfMonth, lastDay); // clamp e.g. day-31 in Feb
      const occ = new Date(yr, mo, day, hh, mm, 0, 0);
      if (isAfter(occ, limit)) break;
      out.push(occ);
      mo += 1;
      if (mo > 11) { mo = 0; yr += 1; }
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const EmailSchedule: React.FC = () => {
  // Anchor of the 3-month window: shows anchor, anchor+1, anchor+2.
  const [anchor, setAnchor] = useState<Date>(() => startOfMonth(new Date()));
  const [clients, setClients] = useState<Client[]>([]);
  const [rows, setRows] = useState<ScheduledEmail[]>([]);
  const [filterClient, setFilterClient] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<ScheduledEmailStatus | ''>('');
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const [editing, setEditing] = useState<Partial<ScheduledEmail> & { _isNew?: boolean } | null>(null);
  const [recurrence, setRecurrence] = useState<RecurrenceState>(() => defaultRecurrenceFor(new Date()));
  const [busy, setBusy] = useState(false);

  // ── Data loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    clientService.getAll().then(setClients).catch(() => setClients([]));
  }, []);

  const windowFrom = useMemo(() => startOfMonth(anchor), [anchor]);
  const windowTo = useMemo(() => endOfMonth(addMonths(anchor, 2)), [anchor]);

  // Active clients only, sorted by agency name (locale-aware, case-insensitive).
  // Pickers should still show the currently-selected client even if it's inactive.
  const activeClients = useMemo(
    () =>
      clients
        .filter((c) => c.active !== false)
        .slice()
        .sort((a, b) =>
          (a.agency_name || '').localeCompare(b.agency_name || '', undefined, {
            sensitivity: 'base',
          }),
        ),
    [clients],
  );

  const loadRows = useCallback(async () => {
    try {
      const list = await listScheduledEmails({
        from: fmtIsoDate(windowFrom),
        to: fmtIsoDate(windowTo),
        client_id: typeof filterClient === 'number' ? filterClient : undefined,
        status: filterStatus || undefined,
      });
      setRows(list);
    } catch (e: any) {
      setSnack({ msg: e?.message || 'Failed to load schedule', severity: 'error' });
    }
  }, [windowFrom, windowTo, filterClient, filterStatus]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Group rows by ISO date (yyyy-MM-dd in local time)
  const rowsByDay = useMemo(() => {
    const m = new Map<string, ScheduledEmail[]>();
    for (const r of rows) {
      const d = parseIsoSafe(r.send_date);
      if (!d) continue;
      const key = fmtIsoDate(d);
      const arr = m.get(key) || [];
      arr.push(r);
      m.set(key, arr);
    }
    // sort each day by time-of-day, then status, then subject
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ad = parseIsoSafe(a.send_date)?.getTime() ?? 0;
        const bd = parseIsoSafe(b.send_date)?.getTime() ?? 0;
        return ad - bd;
      });
    }
    return m;
  }, [rows]);

  // ── Edit dialog actions ──────────────────────────────────────────────────
  const openNew = (day: Date) => {
    const sendAt = new Date(day);
    sendAt.setHours(9, 0, 0, 0); // default 09:00 local
    setEditing({
      _isNew: true,
      client_id: typeof filterClient === 'number' ? filterClient : (clients[0]?.id ?? 0),
      subject: '',
      send_date: sendAt.toISOString(),
      promised_by_date: null,
      status: 'planned',
      platform: 'mautic',
      platform_link_url: null,
      owner_user_id: null,
      audience: null,
      notes: null,
      estimated_recipients: null,
    });
    setRecurrence(defaultRecurrenceFor(sendAt));
  };

  const openEdit = (row: ScheduledEmail) => {
    setEditing({ ...row });
    setRecurrence(defaultRecurrenceFor(parseIsoSafe(row.send_date) || new Date()));
  };

  const closeDialog = () => setEditing(null);

  const saveEditing = async () => {
    if (!editing) return;
    if (!editing.subject || !editing.subject.trim()) {
      setSnack({ msg: 'Subject is required', severity: 'error' });
      return;
    }
    if (!editing.client_id) {
      setSnack({ msg: 'Client is required', severity: 'error' });
      return;
    }
    if (!editing.send_date) {
      setSnack({ msg: 'Send date is required', severity: 'error' });
      return;
    }

    setBusy(true);
    try {
      const payload: ScheduledEmailInput = {
        client_id: editing.client_id as number,
        subject: editing.subject as string,
        send_date: editing.send_date as string,
        promised_by_date: (editing.promised_by_date as string | null) || null,
        status: editing.status as ScheduledEmailStatus,
        platform: editing.platform as ScheduledEmailPlatform,
        platform_link_url: editing.platform_link_url || null,
        owner_user_id: editing.owner_user_id ?? null,
        audience: editing.audience || null,
        notes: editing.notes || null,
        estimated_recipients: editing.estimated_recipients ?? null,
      };

      if (editing._isNew) {
        if (recurrence.enabled) {
          if (!recurrence.until) {
            setSnack({ msg: 'Recurrence end date is required', severity: 'error' });
            return;
          }
          const start = parseIsoSafe(payload.send_date) || new Date();
          const dates = expandRecurrence(start, recurrence);
          if (dates.length === 0) {
            setSnack({ msg: 'No occurrences in that date range', severity: 'error' });
            return;
          }
          const results = await Promise.allSettled(
            dates.map((d) =>
              createScheduledEmail({ ...payload, send_date: d.toISOString() })
            ),
          );
          const ok = results.filter((r) => r.status === 'fulfilled').length;
          const failed = results.length - ok;
          setSnack({
            msg: failed
              ? `Scheduled ${ok} of ${results.length} emails (${failed} failed)`
              : `Scheduled ${ok} emails`,
            severity: failed ? 'error' : 'success',
          });
        } else {
          await createScheduledEmail(payload);
          setSnack({ msg: 'Email scheduled', severity: 'success' });
        }
      } else if (editing.id) {
        await updateScheduledEmail(editing.id, payload);
        setSnack({ msg: 'Email updated', severity: 'success' });
      }
      setEditing(null);
      loadRows();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.detail || e?.message || 'Save failed', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const removeEditing = async () => {
    if (!editing?.id) return;
    if (!window.confirm('Delete this scheduled email?')) return;
    setBusy(true);
    try {
      await deleteScheduledEmail(editing.id);
      setSnack({ msg: 'Deleted', severity: 'success' });
      setEditing(null);
      loadRows();
    } catch (e: any) {
      setSnack({ msg: e?.message || 'Delete failed', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const quickTransition = async (status: ScheduledEmailStatus) => {
    if (!editing?.id) return;
    setBusy(true);
    try {
      const updated = await transitionStatus(editing.id, status);
      setEditing(updated);
      setSnack({ msg: `Status → ${STATUS_LABEL[status]}`, severity: 'success' });
      loadRows();
    } catch (e: any) {
      setSnack({ msg: e?.message || 'Transition failed', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // ── Calendar render ──────────────────────────────────────────────────────
  const months = [0, 1, 2].map((i) => addMonths(anchor, i));

  const headerLabel = months
    .map((m) => format(m, 'MMMM yyyy'))
    .join(' · ');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 'calc(100vh - 120px)' }}>
      {/* Toolbar */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Previous month">
              <IconButton size="small" onClick={() => setAnchor(subMonths(anchor, 1))}>
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next month">
              <IconButton size="small" onClick={() => setAnchor(addMonths(anchor, 1))}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              startIcon={<TodayIcon />}
              onClick={() => setAnchor(startOfMonth(new Date()))}
            >
              Today
            </Button>
          </Stack>

          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>
            {headerLabel}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={filterClient}
              onChange={(e) =>
                setFilterClient(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <MenuItem value="">All clients</MenuItem>
              {activeClients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.agency_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ScheduledEmailStatus | '')}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_ORDER.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openNew(new Date())}
          >
            Add email
          </Button>
        </Stack>

        {/* Status legend */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          {STATUS_ORDER.map((s) => (
            <Chip
              key={s}
              size="small"
              label={STATUS_LABEL[s]}
              sx={{
                bgcolor: STATUS_COLOR[s].bg,
                color: STATUS_COLOR[s].fg,
                border: `1px solid ${STATUS_COLOR[s].border}`,
                textDecoration: s === 'canceled' ? 'line-through' : 'none',
              }}
            />
          ))}
        </Stack>
      </Paper>

      {/* 3-month grid */}
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          flex: 1,
        }}
      >
        {months.map((m) => (
          <MonthGrid
            key={m.toISOString()}
            month={m}
            rowsByDay={rowsByDay}
            onAdd={openNew}
            onEdit={openEdit}
          />
        ))}
      </Box>

      {/* Edit dialog */}
      <EditDialog
        editing={editing}
        clients={activeClients}
        allClients={clients}
        busy={busy}
        recurrence={recurrence}
        onRecurrenceChange={(patch) => setRecurrence((cur) => ({ ...cur, ...patch }))}
        onChange={(patch) => setEditing((cur) => (cur ? { ...cur, ...patch } : cur))}
        onClose={closeDialog}
        onSave={saveEditing}
        onDelete={removeEditing}
        onQuickTransition={quickTransition}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default EmailSchedule;

// ─────────────────────────────────────────────────────────────────────────────
// Month grid sub-component
// ─────────────────────────────────────────────────────────────────────────────

interface MonthGridProps {
  month: Date;
  rowsByDay: Map<string, ScheduledEmail[]>;
  onAdd: (day: Date) => void;
  onEdit: (row: ScheduledEmail) => void;
}

const MAX_PILLS_PER_DAY = 2;

const MonthGrid: React.FC<MonthGridProps> = ({ month, rowsByDay, onAdd, onEdit }) => {
  const today = useMemo(() => startOfDay(new Date()), []);

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weekHeader = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, px: 0.5, pb: 0.5 }}>
        {format(month, 'MMMM yyyy')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          fontSize: 12,
        }}
      >
        {weekHeader.map((w) => (
          <Box
            key={w}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'text.secondary',
              textAlign: 'center',
              py: 0.25,
            }}
          >
            {w}
          </Box>
        ))}
        {days.map((d) => {
          const inMonth = isSameMonth(d, month);
          const isToday = isSameDay(d, today);
          const entries = rowsByDay.get(fmtIsoDate(d)) || [];
          const shown = entries.slice(0, MAX_PILLS_PER_DAY);
          const overflow = entries.length - shown.length;

          return (
            <Box
              key={d.toISOString()}
              onClick={(e) => {
                // ignore if a pill caught the click
                if ((e.target as HTMLElement).dataset.pill) return;
                onAdd(d);
              }}
              sx={{
                minHeight: 72,
                bgcolor: inMonth ? 'background.paper' : 'action.hover',
                border: isToday ? '2px solid' : '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                borderRadius: 0.5,
                p: 0.5,
                cursor: 'pointer',
                position: 'relative',
                '&:hover': { bgcolor: inMonth ? 'action.hover' : 'action.selected' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'right',
                  color: inMonth ? 'text.primary' : 'text.disabled',
                  fontWeight: isToday ? 700 : 400,
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {format(d, 'd')}
              </Typography>
              <Stack spacing={0.25}>
                {shown.map((r) => (
                  <Tooltip
                    key={r.id}
                    title={
                      <>
                        <div><b>{r.subject}</b></div>
                        <div>{r.client_name || `client ${r.client_id}`}</div>
                        <div>{STATUS_LABEL[r.status]} · {r.platform}</div>
                      </>
                    }
                  >
                    <Box
                      data-pill="1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(r);
                      }}
                      sx={{
                        bgcolor: STATUS_COLOR[r.status].bg,
                        color: STATUS_COLOR[r.status].fg,
                        border: `1px solid ${STATUS_COLOR[r.status].border}`,
                        borderRadius: 0.5,
                        px: 0.5,
                        py: 0.1,
                        fontSize: 10.5,
                        lineHeight: 1.2,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        textDecoration: r.status === 'canceled' ? 'line-through' : 'none',
                        '&:hover': { filter: 'brightness(0.95)' },
                      }}
                    >
                      {r.subject}
                    </Box>
                  </Tooltip>
                ))}
                {overflow > 0 && (
                  <Typography
                    data-pill="1"
                    variant="caption"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Open the first hidden one — coarse but useful UX
                      onEdit(entries[MAX_PILLS_PER_DAY]);
                    }}
                    sx={{ color: 'text.secondary', cursor: 'pointer', fontSize: 10 }}
                  >
                    +{overflow} more
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit dialog
// ─────────────────────────────────────────────────────────────────────────────

interface EditDialogProps {
  editing: (Partial<ScheduledEmail> & { _isNew?: boolean }) | null;
  clients: Client[];      // already filtered to active + sorted
  allClients: Client[];   // full list, used to look up an inactive selection
  busy: boolean;
  recurrence: RecurrenceState;
  onRecurrenceChange: (patch: Partial<RecurrenceState>) => void;
  onChange: (patch: Partial<ScheduledEmail>) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  onQuickTransition: (status: ScheduledEmailStatus) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({
  editing,
  clients,
  allClients,
  busy,
  recurrence,
  onRecurrenceChange,
  onChange,
  onClose,
  onSave,
  onDelete,
  onQuickTransition,
}) => {
  if (!editing) return null;

  // If editing a row whose client is now inactive, surface it in the picker
  // so the value doesn't render as blank.
  const selectedClient = allClients.find((c) => c.id === editing.client_id);
  const showInactive =
    selectedClient && selectedClient.active === false ? selectedClient : null;
  const clientOptions = showInactive ? [...clients, showInactive] : clients;

  const sendDateLocal = (() => {
    const d = parseIsoSafe(editing.send_date as string | null);
    return d ? fmtIsoLocalDateTime(d) : '';
  })();

  const occurrenceCount = (() => {
    if (!editing._isNew || !recurrence.enabled) return 0;
    const start = parseIsoSafe(editing.send_date as string | null);
    if (!start) return 0;
    return expandRecurrence(start, recurrence).length;
  })();

  return (
    <Dialog open={!!editing} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing._isNew ? 'New scheduled email' : `Edit: ${editing.subject || ''}`}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          <TextField
            label="Subject / working title"
            value={editing.subject ?? ''}
            onChange={(e) => onChange({ subject: e.target.value })}
            required
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />

          <FormControl required>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={editing.client_id ?? ''}
              onChange={(e) => onChange({ client_id: Number(e.target.value) })}
            >
              {clientOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.agency_name}
                  {c.active === false ? ' (inactive)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={editing.status ?? 'planned'}
              onChange={(e) => onChange({ status: e.target.value as ScheduledEmailStatus })}
            >
              {STATUS_ORDER.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Send date"
            type="datetime-local"
            value={sendDateLocal}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              onChange({ send_date: new Date(v).toISOString() });
            }}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Promised by"
            type="date"
            value={(editing.promised_by_date as string) ?? ''}
            onChange={(e) => onChange({ promised_by_date: e.target.value || null })}
            InputLabelProps={{ shrink: true }}
            helperText="When this was promised to the client"
          />

          <FormControl>
            <InputLabel>Platform</InputLabel>
            <Select
              label="Platform"
              value={editing.platform ?? 'mautic'}
              onChange={(e) => onChange({ platform: e.target.value as ScheduledEmailPlatform })}
            >
              {PLATFORMS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Platform link URL"
            value={editing.platform_link_url ?? ''}
            onChange={(e) => onChange({ platform_link_url: e.target.value || null })}
            placeholder="https://…/email/edit/…"
            helperText="URL to view this email in Mautic/Brevo"
            InputProps={{
              endAdornment: editing.platform_link_url ? (
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => window.open(editing.platform_link_url as string, '_blank')}
                  title="Open in platform"
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />

          <TextField
            label="Audience / segment"
            value={editing.audience ?? ''}
            onChange={(e) => onChange({ audience: e.target.value || null })}
            placeholder="e.g. Newsletter subscribers, active leads"
          />

          <TextField
            label="Estimated recipients"
            type="number"
            value={editing.estimated_recipients ?? ''}
            onChange={(e) =>
              onChange({
                estimated_recipients: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            inputProps={{ min: 0 }}
          />

          <TextField
            label="Notes / brief"
            value={editing.notes ?? ''}
            onChange={(e) => onChange({ notes: e.target.value || null })}
            multiline
            minRows={3}
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
        </Box>

        {/* Repeat (new emails only) */}
        {editing._isNew && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={recurrence.enabled}
                  onChange={(_, v) => onRecurrenceChange({ enabled: v })}
                />
              }
              label="Repeat"
            />
            {recurrence.enabled && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    mt: 1,
                  }}
                >
                  <FormControl size="small">
                    <InputLabel>Repeats</InputLabel>
                    <Select
                      label="Repeats"
                      value={recurrence.freq}
                      onChange={(e) =>
                        onRecurrenceChange({ freq: e.target.value as RecurrenceFreq })
                      }
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>

                  {recurrence.freq === 'weekly' && (
                    <FormControl size="small">
                      <InputLabel>On</InputLabel>
                      <Select
                        label="On"
                        value={recurrence.weekday}
                        onChange={(e) =>
                          onRecurrenceChange({ weekday: Number(e.target.value) })
                        }
                      >
                        {WEEKDAYS.map((d, i) => (
                          <MenuItem key={i} value={i}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {recurrence.freq === 'monthly' && (
                    <TextField
                      size="small"
                      label="Day of month"
                      type="number"
                      value={recurrence.dayOfMonth}
                      inputProps={{ min: 1, max: 31 }}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isNaN(n)) return;
                        onRecurrenceChange({
                          dayOfMonth: Math.max(1, Math.min(31, n || 1)),
                        });
                      }}
                      helperText={
                        recurrence.dayOfMonth > 28
                          ? 'Clamped to last day for short months'
                          : undefined
                      }
                    />
                  )}

                  {recurrence.freq === 'daily' && <Box />}

                  <TextField
                    size="small"
                    label="Until"
                    type="date"
                    value={recurrence.until}
                    onChange={(e) => onRecurrenceChange({ until: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}
                >
                  {occurrenceCount === 0
                    ? 'Pick an end date after the send date.'
                    : `Will create ${occurrenceCount} email${occurrenceCount === 1 ? '' : 's'}.`}
                  {occurrenceCount >= MAX_OCCURRENCES && ` (capped at ${MAX_OCCURRENCES})`}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Quick transition row (only for existing rows) */}
        {!editing._isNew && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Quick status change:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {STATUS_ORDER.map((s) => (
                <Chip
                  key={s}
                  label={STATUS_LABEL[s]}
                  size="small"
                  variant={editing.status === s ? 'filled' : 'outlined'}
                  onClick={() => onQuickTransition(s)}
                  disabled={busy || editing.status === s}
                  sx={{
                    bgcolor: editing.status === s ? STATUS_COLOR[s].bg : undefined,
                    color: editing.status === s ? STATUS_COLOR[s].fg : undefined,
                    borderColor: STATUS_COLOR[s].border,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {!editing._isNew ? (
          <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={busy}>
            Delete
          </Button>
        ) : (
          <span />
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={busy}>
            {editing._isNew ? 'Create' : 'Save'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
